import { Link, useLocation, useNavigate } from "react-router-dom";
import { Zap, Menu, X } from "lucide-react";
import { useState } from "react";
import { loadUser, logout } from "@/lib/api";

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const user = loadUser();

  const isActive = (p) => loc.pathname === p;
  const NavItem = ({ to, children, tid }) => (
    <Link
      to={to}
      data-testid={tid}
      onClick={() => setOpen(false)}
      className={`block px-4 py-2 text-sm tracking-wider uppercase font-medium ${
        isActive(to) ? "text-[#CCFF00]" : "text-zinc-300 hover:text-white"
      }`}
    >
      {children}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0B0E]/85 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2">
          <Zap className="text-[#CCFF00]" size={22} />
          <span className="font-display text-xl sm:text-2xl">NEXT GEN SPOTLESS</span>
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <NavItem to="/services" tid="nav-services">Services</NavItem>
          <NavItem to="/book" tid="nav-request">Request Detail</NavItem>
          <NavItem to="/group" tid="nav-group">Book & Save Together</NavItem>
          <NavItem to="/about" tid="nav-about">About</NavItem>
          {user ? (
            <>
              <NavItem
                to={user.role === "admin" ? "/admin" : "/employee"}
                tid="nav-portal"
              >
                {user.role === "admin" ? "Admin" : "Portal"}
              </NavItem>
              <button
                data-testid="logout-btn"
                onClick={() => { logout(); nav("/"); }}
                className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white"
              >Sign out</button>
            </>
          ) : (
            <NavItem to="/login" tid="nav-login">Staff Login</NavItem>
          )}
          <Link to="/book" data-testid="header-cta-book" className="neon-btn px-5 py-2 rounded-xl text-xs">
            Book Now
          </Link>
        </nav>
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0A0B0E]/95">
          <NavItem to="/services" tid="mnav-services">Services</NavItem>
          <NavItem to="/book" tid="mnav-request">Request Detail</NavItem>
          <NavItem to="/group" tid="mnav-group">Book & Save Together</NavItem>
          <NavItem to="/about" tid="mnav-about">About</NavItem>
          {user ? (
            <NavItem to={user.role === "admin" ? "/admin" : "/employee"} tid="mnav-portal">
              {user.role === "admin" ? "Admin" : "Portal"}
            </NavItem>
          ) : (
            <NavItem to="/login" tid="mnav-login">Staff Login</NavItem>
          )}
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-white/10 bg-[#0A0B0E]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <div className="font-display text-2xl text-[#CCFF00]">NEXT GEN SPOTLESS</div>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
            Clean cars. Strong community. Better future.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Quick Links</div>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/services" className="text-zinc-300 hover:text-[#CCFF00]">Services</Link>
            <Link to="/book" className="text-zinc-300 hover:text-[#CCFF00]">Request a Detail</Link>
            <Link to="/group" className="text-zinc-300 hover:text-[#CCFF00]">Book & Save Together</Link>
            <Link to="/about" className="text-zinc-300 hover:text-[#CCFF00]">About / Contact</Link>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Contact</div>
          <div className="text-sm text-zinc-400 space-y-1.5">
            <div>
              <a href="tel:+17045939023" data-testid="footer-phone" className="hover:text-[#CCFF00]">704-593-9023</a>
            </div>
            <div>
              <a href="mailto:NextGenSpotless1@gmail.com" data-testid="footer-email" className="hover:text-[#CCFF00] break-all">NextGenSpotless1@gmail.com</a>
            </div>
            <div>
              <a href="https://www.instagram.com/nextgenspotless" target="_blank" rel="noopener noreferrer"
                data-testid="footer-instagram" className="hover:text-[#CCFF00]">@nextgenspotless on Instagram</a>
            </div>
            <div className="text-xs text-zinc-500 pt-2">Mobile service — we come to you.</div>
          </div>
        </div>
      </div>
      <div className="text-center text-xs text-zinc-600 pb-6">© {new Date().getFullYear()} Next Gen Spotless</div>
    </footer>
  );
}
